import React, { useEffect, FC } from 'react'
import { Route, Routes } from 'react-router-dom'
import { Box } from '@mui/material'
import { AppShell } from '@e3dc-react/shell/Elements/AppShell'
import { Relogin } from '@e3dc-react/shell/Elements/Relogin'
import { PageAnalytics } from '@e3dc-react/shell/Elements'
import { store, persistor } from './State/Store'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import Framework from './Elements/Framework/Framework'
import { BatteryProvider } from './Libs/batteries/providers'
import { PowerMeterProvider } from './Libs/power-meters'
import InstallationAccessRightsProvider from './Libs/installation-rights-management/provider/InstallationAccessRightsProvider'
import NotificationProvider from './Libs/notifications/context/notification-provider'
import AccountLinksProvider from './Libs/installation-rights-management/provider/AccountLinksProvider'
import InstallationsProvider from './Libs/installations/context/installations-provider'
import FrameworkRoutes from './Routes/FrameworkRoutes'
import { CookieProvider } from './Libs/cookies'
import ActiveInstallationProvider from './Libs/installations/context/active-installation-provider'
import ErrorComponent from './Routes/Error/UnhandledErrorPage'
import { changeAppLanguage, getCurrentLanguage } from './Libs/languages/functions/default'
import moment from '@e3dc-react/shell/Libs/moment'
import { ObservabilityAnalytics } from './Libs/observability-tool'
import EMobilityAccessRightsProvider from './Libs/system-settings/provider/EMobilityAccessRightsProvider'
import { useInitUserData } from './Libs/user/hooks'
import MaintenancePageWrapper from './Libs/maintenance-page/components/MaintenancePageWrapper'
import useDefaultUserRoutes from './Libs/navigation/hooks/use-default-user-routes'
import useDefaultShopRoutes from './Libs/navigation/hooks/use-default-shop-routes'
import useGeneralRoutes from './Libs/navigation/hooks/use-general-routes'
import NdaCheckWrapper from './Elements/NdaCheck/NdaCheckWrapper'
import FeatBitProvider from './Libs/feature-flags/FeatBitProvider'
import { Helmet } from 'react-helmet'
import { useTypedSelector } from './State/RootReducer'
import { useClearCacheOnVersionChange, useIsWebviewMode } from './Hooks'
import useUserLanguage from './Libs/user/hooks/use-user-language'
import i18n from './Configuration/i18n'
import { useSetYupLocalization } from './Configuration/validation/use-set-yup-localization'
import EnforceIosUpdateWrapper from './Libs/mobile-app-update/components/EnforceIosUpdateWrapper'
import { DownloadsProvider } from './Libs/downloads'
import { WallboxLabelProvider } from './Libs/e-mobility/shared/providers/WallboxLabelProvider'
import { HagerBridgePermissionProvider } from 'src/Libs/e-mobility/shared'
import { useThemedFavicon } from './Libs/utils'
import { useNormalizedPathNames } from './Libs/observability-tool/hooks/use-normalized-path-names'
import './Configuration/validation/set-zod-localization'
import FlowThemeAppVersionEnforcer from './Libs/mobile-app-update/components/FlowThemeAppVersionEnforcer'

const IS_PRODUCTION_ENV = process.env.NODE_ENV === 'production'
const ANALYTICS_ID = process.env.REACT_APP_ANALYTICS_ID
const ANALYTICS_URL = process.env.REACT_APP_ANALYTICS_URL
const BASE_PAGE_URL = process.env.REACT_APP_BASEURL
const WEBVIEW_USER_AGENT_KEY = 'Portal'
const NO_SCROLL_CLASSNAME = 'no-scroll'

const App: FC = () => {
  return (
    <AppShell store={store} persistor={persistor} errorComponent={<ErrorComponent />}>
      <Content />
    </AppShell>
  )
}

/**
 * content is a seperate component since it uses redux hooks which are not available until AppShell mounted!
 */
const Content: FC = () => {
  const { userLanguage, isUserLanguageLoading } = useUserLanguage()
  useEffect(() => {
    if (userLanguage && !isUserLanguageLoading) {
      localStorage.setItem('appLanguage', userLanguage)
      const shouldChangeLanguage = i18n.language !== userLanguage || i18n.resolvedLanguage !== userLanguage
      if (shouldChangeLanguage) changeAppLanguage(userLanguage)
    }
    const appLanguage = userLanguage ?? getCurrentLanguage()
    moment.locale(appLanguage)
  }, [userLanguage, isUserLanguageLoading])

  useClearCacheOnVersionChange()
  useThemedFavicon()
  useSetYupLocalization(userLanguage)

  const { user, userLoading } = useInitUserData()
  const defaultUserRoutes = useDefaultUserRoutes()
  const defaultShopRoutes = useDefaultShopRoutes({ user, userLoading })
  const generalRoutes = useGeneralRoutes()
  const isWebview = useIsWebviewMode()
  const moreActive = useTypedSelector(state => state.app.moreActive)
  useNormalizedPathNames()

  if (isUserLanguageLoading && !i18n.isInitialized) {
    return <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }} />
  }

  return (
    <>
      {IS_PRODUCTION_ENV && ANALYTICS_ID && ANALYTICS_URL && BASE_PAGE_URL && (
        <PageAnalytics
          analyticsId={ANALYTICS_ID}
          analyticsUrl={ANALYTICS_URL}
          basePageUrl={BASE_PAGE_URL}
          webViewParameter={WEBVIEW_USER_AGENT_KEY}
        />
      )}
      <Helmet>
        {isWebview && <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />}
        {/* prevent scrolling the content when mobile sidebar is open */}
        <body className={moreActive ? NO_SCROLL_CLASSNAME : ''} />
      </Helmet>
      <FeatBitProvider>
        <FlowThemeAppVersionEnforcer>
          <EnforceIosUpdateWrapper>
            <ObservabilityAnalytics user={user} />
            <CookieProvider showCookieBanner={'hidden'}>
              <Routes>
                {generalRoutes}
                {defaultUserRoutes}
                <Route
                  path="*"
                  element={
                    <Relogin>
                      <Routes>
                        {defaultShopRoutes}
                        <Route
                          path="*"
                          element={
                            <InstallationsProvider>
                              <ActiveInstallationProvider>
                                <HagerBridgePermissionProvider userData={user}>
                                  <WallboxLabelProvider>
                                    <NotificationProvider>
                                      <AccountLinksProvider>
                                        <DownloadsProvider>
                                          <MaintenancePageWrapper>
                                            <NdaCheckWrapper user={user ?? null} userLoading={userLoading}>
                                              <InstallationAccessRightsProvider
                                                companyId={user?.Firmen_ID}
                                                userLevel={user?.level}
                                                userName={user?.username}
                                                ssoId={user?.ssoId}
                                              >
                                                <EMobilityAccessRightsProvider
                                                  companyId={user?.Firmen_ID}
                                                  userLevel={user?.level}
                                                  userName={user?.username}
                                                >
                                                  <PowerMeterProvider>
                                                    <BatteryProvider>
                                                      <Framework withContent={true}>{user && <FrameworkRoutes user={user} />}</Framework>
                                                    </BatteryProvider>
                                                  </PowerMeterProvider>
                                                </EMobilityAccessRightsProvider>
                                              </InstallationAccessRightsProvider>
                                            </NdaCheckWrapper>
                                          </MaintenancePageWrapper>
                                        </DownloadsProvider>
                                      </AccountLinksProvider>
                                    </NotificationProvider>
                                  </WallboxLabelProvider>
                                </HagerBridgePermissionProvider>
                              </ActiveInstallationProvider>
                            </InstallationsProvider>
                          }
                        />
                      </Routes>
                    </Relogin>
                  }
                />
              </Routes>
            </CookieProvider>
          </EnforceIosUpdateWrapper>
        </FlowThemeAppVersionEnforcer>
      </FeatBitProvider>
    </>
  )
}

export default App
